// Imports
import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Chart from 'react-apexcharts'

// UI Imports
import { Typography, Toolbar, Button, Grid, Card, CardContent, Box, Container, LinearProgress, Avatar } from "@mui/material"
import {
  Money,
  ShoppingCart as OrderIcon,
  Payment as PaymentIcon,
  Restaurant as TableIcon,
  QrCode as QrIcon,
  AttachMoney as SalesIcon,
  Category as CategoryIcon,
  Inventory as ProductIcon,
  People as CustomerIcon,
  Contacts as ContactIcon,
  TrendingUp as LeadIcon,
  Star as ProspectIcon,
  VerifiedUser as CustomerStatusIcon,
  Event as AppointmentIcon,
  CalendarToday as TodayIcon,
  DateRange as WeekIcon,
  EventNote as MonthIcon,
  Functions as TotalIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Storage as StorageIcon,
} from "@mui/icons-material"
import { withStyles } from "@material-ui/core/styles"
import styles from './styles'
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

// App Imports
import { getDashboardCounts } from '../api/actions/query'
import Section from '../../common/Section'
import BrandedLoading from '../../common/BrandedLoading'
import params from '../../../setup/config/params'
import DashboardCharts from '../Charts/DashboardChart'
import Footer from '../../common/Footer'
import { settingDetail } from "../../setting/api/actions/query"

// Component
class Dashboard extends PureComponent {
  state = {
    openChart: null,
    selectedLeadStatus: null, // Track which lead status was clicked (Contact, Lead, Prospect, or Customer)
    settings: {
      isStoreSubscribed: false,
      showGrowthMenu: false,
      isTableReservationEnabled: false,
      isQrDiningEnabled: false,
    },
    selectedAppointmentPeriod: null,
    currentChartPage: 0, // For appointment chart pagination
    counts: {
      order: [],
      payment: [],
      tableBooking: [],
      qrOrders: [],
      product: 0,
      category: 0,
      customer: [],
      crmData: [], // Array of CRM records with leadStatus
      appointmentBooking: null, // Will store appointment booking data
    },
    isLoading: false,
    initialLoading: true, // Prevent "No data available" from showing on first load
  };

  componentDidMount() {
    this.refresh()()
    this.fetchSettings()
  }

  refresh = () => async () => {
    const { getDashboardCounts } = this.props
    try {
      await getDashboardCounts()
    } finally {
      // After first load completes, set initialLoading to false
      if (this.state.initialLoading) {
        this.setState({ initialLoading: false })
      }
    }
  }

  fetchSettings = async () => {
    const { settingDetail } = this.props
    try {
      const { data } = await settingDetail()
      if (data.success && data.data) {
        this.setState({ settings: data.data })
      }
    } catch (error) {
      console.error("Error fetching setting details:", error)
    }
  }

  handleOpenChart = (chartType, leadStatus = null) => {
    this.setState({
      openChart: chartType,
      selectedLeadStatus: leadStatus // Store which lead status was clicked
    })
  }

  handleCloseChart = () => {
    this.setState({
      openChart: null,
      selectedLeadStatus: null // Reset lead status filter when closing
    })
  }

  calculateLeadStatusCounts = () => {
    const { dashboardCounts: { counts } } = this.props
    const { crmData = [] } = counts

    // Calculate counts for each lead status
    const leadStatusCounts = crmData.reduce((acc, record) => {
      const status = record.leadStatus
      if (status) {
        acc[status] = (acc[status] || 0) + 1
      }
      return acc
    }, {})

    return leadStatusCounts
  }


  handleOpenAppointmentChart = (period) => {
    this.setState({ selectedAppointmentPeriod: period })
  }

  handleCloseAppointmentChart = () => {
    this.setState({ selectedAppointmentPeriod: null })
  }

  getAppointmentChartData = (period) => {
    const { dashboardCounts: { counts } } = this.props
    const appointmentBookings = counts.appointmentBooking
    
    // Check if data exists and is an array
    if (!appointmentBookings || !Array.isArray(appointmentBookings) || appointmentBookings.length === 0) return []

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    switch (period) {
      case 'weekly':
        // Group by week number across all data
        const weeklyData = {}
        appointmentBookings.forEach(booking => {
          const bookingDate = new Date(booking.createdAt)
          const year = bookingDate.getFullYear()
          
          // Calculate week number
          const startOfYear = new Date(year, 0, 1)
          const dayOfYear = Math.floor((bookingDate - startOfYear) / (24 * 60 * 60 * 1000))
          const weekNumber = Math.ceil((dayOfYear + startOfYear.getDay() + 1) / 7)
          
          const weekLabel = `W${weekNumber} - ${year}`
          weeklyData[weekLabel] = (weeklyData[weekLabel] || 0) + 1
        })
        
        // Sort by year and week number
        return Object.entries(weeklyData)
          .sort((a, b) => {
            const [weekA, yearA] = a[0].match(/W(\d+) - (\d+)/).slice(1)
            const [weekB, yearB] = b[0].match(/W(\d+) - (\d+)/).slice(1)
            if (yearA !== yearB) return yearA - yearB
            return weekA - weekB
          })
          .map(([x, y]) => ({ x, y }))

      case 'monthly':
        // Group by month across all data
        const monthlyData = {}
        appointmentBookings.forEach(booking => {
          const bookingDate = new Date(booking.createdAt)
          const month = bookingDate.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric'
          })
          monthlyData[month] = (monthlyData[month] || 0) + 1
        })
        
        // Sort by year and month
        return Object.entries(monthlyData)
          .sort((a, b) => {
            const dateA = new Date(a[0])
            const dateB = new Date(b[0])
            return dateA - dateB
          })
          .map(([x, y]) => ({ x, y }))

      default:
        return []
    }
  }

  // Paginate chart data
  getPaginatedChartData = (data, page = 0, itemsPerPage = 20) => {
    if (!data || data.length === 0) return { paginatedData: [], totalPages: 0 }
    
    const totalPages = Math.ceil(data.length / itemsPerPage)
    const startIndex = page * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = data.slice(startIndex, endIndex)
    
    return { paginatedData, totalPages }
  }

  renderChartDialog() {
    const { openChart, selectedLeadStatus } = this.state
    const { dashboardCounts: { counts } } = this.props

    // Get the title text
    let titleText = "";
    if (openChart === "orders") titleText = "Order Chart";
    else if (openChart === "payments") titleText = "Payments Chart";
    else if (openChart === "customers") titleText = "New Customers Chart";
    else if (openChart === "sales") titleText = "Sales Chart";
    else if (openChart === "tableBookings") titleText = "Table Booking Chart";
    else if (openChart === "qrOrders") titleText = "QR Dining Chart";
    else if (openChart === "crm" && selectedLeadStatus) titleText = selectedLeadStatus;
    else if (openChart === "appointments") titleText = "Appointment Bookings";
    else if (openChart === "appointmentPayments") titleText = "Appointment Payments";
    else if (openChart === "storage") titleText = "S3 Storage Usage Details";
    else if (openChart === "emailUsage") titleText = "Email Usage History";
    else if (openChart === "smsUsage") titleText = "SMS Usage History";

    return (
      <Dialog open={!!openChart} onClose={this.handleCloseChart} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {titleText}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={this.handleCloseChart}
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DashboardCharts 
            chartType={openChart} 
            counts={counts} 
            selectedLeadStatus={selectedLeadStatus} 
            appointments={counts.appointmentBooking || []} 
            storageUsage={counts.storageUsage}
          />
        </DialogContent>
      </Dialog>
    )
  }

  renderAppointmentChartDialog() {
    const { selectedAppointmentPeriod, currentChartPage } = this.state
    if (!selectedAppointmentPeriod) return null
    const allChartData = this.getAppointmentChartData(selectedAppointmentPeriod)
    const { paginatedData: chartData, totalPages } = this.getPaginatedChartData(allChartData, currentChartPage, 20)
    
    const chartOptions = {
      chart: {
        type: 'bar',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          },
          export: {
            csv: {
              filename: `Appointment_Bookings_${selectedAppointmentPeriod}_${new Date().toISOString().split('T')[0]}`,
              headerCategory: 'Period',
              dateFormatter(timestamp) {
                return new Date(timestamp).toDateString()
              }
            },
            svg: {
              filename: `Appointment_Bookings_${selectedAppointmentPeriod}_${new Date().toISOString().split('T')[0]}`
            },
            png: {
              filename: `Appointment_Bookings_${selectedAppointmentPeriod}_${new Date().toISOString().split('T')[0]}`
            }
          }
        },
        zoom: { enabled: false },
        fontFamily: "'Inter', sans-serif",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%',
          distributed: false
        }
      },
      xaxis: {
        categories: chartData.map(d => d.x),
        labels: {
          rotate: 0,
          style: {
            fontSize: '12px',
            colors: '#8898aa'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.floor(val),
          style: {
            fontSize: '12px',
            colors: '#8898aa'
          }
        },
        min: 0,
        title: {
          style: { color: '#8898aa', fontSize: '12px', fontWeight: 500 }
        }
      },
      title: {
        text: `Appointment Bookings (${selectedAppointmentPeriod === 'weekly' ? "Weekly" : "Monthly"})`,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 600,
          color: '#32325d'
        }
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#f3f4f6',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 20, bottom: 0, left: 10 }
      },
      colors: ['#5e72e4'],
      tooltip: {
        theme: 'light',
        y: { formatter: (val) => `${val} bookings` }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        offsetY: -20,
        markers: { radius: 12 },
        itemMargin: { horizontal: 10 }
      }
    }

    const series = [
      {
        name: 'Bookings',
        data: chartData.map(d => d.y)
      }
    ]

    const periods = [
      { label: 'Monthly', value: 'monthly' },
      { label: 'Weekly', value: 'weekly' }
    ]

    return (
      <Dialog open={true} onClose={this.handleCloseAppointmentChart} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Appointment Bookings
            </Typography>
            <IconButton
              aria-label="close"
              onClick={this.handleCloseAppointmentChart}
              sx={{ position: 'absolute', right: 10, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 4 }}>
          {/* Period toggle buttons centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              backgroundColor: '#f3f4f6',
              borderRadius: '12px',
              padding: '4px',
              gap: '4px'
            }}>
              {periods.map((period) => (
                <Button
                  key={period.value}
                  variant="text"
                  onClick={() =>{this.setState({ selectedAppointmentPeriod: period.value, currentChartPage: 0 })}}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: '8px',
                    backgroundColor: selectedAppointmentPeriod === period.value ? '#fff' : 'transparent',
                    color: selectedAppointmentPeriod === period.value ? '#1f2937' : '#0d3559',
                    boxShadow: selectedAppointmentPeriod === period.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    '&:hover': {
                      backgroundColor: selectedAppointmentPeriod === period.value ? '#fff' : 'rgba(13, 53, 89, 0.04)'
                    },
                    minWidth: '90px'
                  }}
                >
                  {period.label}
                </Button>
              ))}
            </Box>
          </Box>
          <Chart options={chartOptions} series={series} type="bar" height={400} />
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={currentChartPage === 0}
                onClick={() => this.setState({ currentChartPage: currentChartPage - 1 })}
                sx={{ textTransform: 'none' }}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Page {currentChartPage + 1} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentChartPage >= totalPages - 1}
                onClick={() => this.setState({ currentChartPage: currentChartPage + 1 })}
                sx={{ textTransform: 'none' }}
              >
                Next
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    )
  }

  renderStatCard(title, value, icon, color, onClick) {
    return (
      <Grid item xs={12} sm={6} md={3} lg={2.4}>
        <Card
          sx={{
            height: '100%',
            cursor: onClick ? 'pointer' : 'default',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': onClick
              ? {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
              }
              : {},
            bgcolor: '#fff',
            border: '1px solid #f1f5f9',
            position: 'relative',
            overflow: 'hidden',
          }}
          onClick={onClick}
          elevation={0}
        >
          <CardContent
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'space-between',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '10px',
                  bgcolor: `${color}15`,
                  color: color,
                }}
              >
                {React.cloneElement(icon, { style: { fontSize: 22 } })}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#9ca3af',
                  letterSpacing: '0.04em',
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* Value */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
            </Box>

            {/* Decorative Circle */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -14,
                right: -14,
                width: 70,
                height: 70,
                borderRadius: '50%',
                bgcolor: `${color}05`,
                zIndex: 0,
              }}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  renderUsageOverview() {
    const { dashboardCounts: { counts } } = this.props
    const emailUsage = counts?.emailUsage
    const smsUsage = counts?.smsUsage
    const storageUsage = counts?.storageUsage

    if (!storageUsage && !emailUsage && !smsUsage) return null

    return (
      <Box sx={{ mt: 6, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1f2937' }}>
          Resource Usage
        </Typography>
        <Grid container spacing={2}>
          {emailUsage && (
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
              {/* Email Quota Card */}
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                  bgcolor: '#fff',
                  border: '1px solid #f1f5f9',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
                  }
                }}
                onClick={() => this.handleOpenChart("emailUsage")}
              >
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      width: 32, 
                      height: 32, 
                      borderRadius: '10px', 
                      bgcolor: '#5e72e415', 
                      color: '#5e72e4' 
                    }}>
                      <EmailIcon style={{ fontSize: 22 }} />
                    </Box>
                    {emailUsage.current >= emailUsage.limit ? (
                      <Box sx={{ 
                        bgcolor: '#f5365c', 
                        color: '#fff', 
                        px: 1, 
                        py: 0.2, 
                        borderRadius: '6px', 
                        fontSize: '9px', 
                        fontWeight: 900,
                        letterSpacing: '0.05em'
                      }}>
                        LIMIT EXCEEDED
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#9ca3af', letterSpacing: '0.04em' }}>
                        Emails Quota
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 800, 
                      color: '#111827', 
                      lineHeight: 1.2 
                    }}>
                      {emailUsage.current} / {emailUsage.limit}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Plan: {emailUsage.plan.charAt(0).toUpperCase() + emailUsage.plan.slice(1).toLowerCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 1.5 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((emailUsage.current / emailUsage.limit) * 100, 100)} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: emailUsage.current >= emailUsage.limit ? '#f5365c' : '#5e72e4',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                    
                    {emailUsage.current >= emailUsage.limit && (
                      <Typography variant="caption" sx={{ color: '#f5365c', display: 'block', mt: 1, fontWeight: 700, fontSize: '10px' }}>
                        Emails are currently blocked.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          {smsUsage && (
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                  bgcolor: '#fff',
                  border: '1px solid #f1f5f9',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
                  }
                }}
                onClick={() => this.handleOpenChart("smsUsage")}
              >
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      width: 32, 
                      height: 32, 
                      borderRadius: '10px', 
                      bgcolor: '#11cdef15', 
                      color: '#11cdef' 
                    }}>
                      <MessageIcon style={{ fontSize: 22 }} />
                    </Box>
                    {smsUsage.current >= smsUsage.limit ? (
                      <Box sx={{ 
                        bgcolor: '#f5365c', 
                        color: '#fff', 
                        px: 1, 
                        py: 0.2, 
                        borderRadius: '6px', 
                        fontSize: '9px', 
                        fontWeight: 900,
                        letterSpacing: '0.05em'
                      }}>
                        LIMIT EXCEEDED
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#9ca3af', letterSpacing: '0.04em' }}>
                        SMS Quota
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 800, 
                      color: '#111827', 
                      lineHeight: 1.2 
                    }}>
                      {smsUsage.current} / {smsUsage.limit}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Plan: {smsUsage.plan.charAt(0).toUpperCase() + smsUsage.plan.slice(1).toLowerCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 1.5 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((smsUsage.current / smsUsage.limit) * 100, 100)} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: smsUsage.current >= smsUsage.limit ? '#f5365c' : '#11cdef',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                    
                    {smsUsage.current >= smsUsage.limit && (
                      <Typography variant="caption" sx={{ color: '#f5365c', display: 'block', mt: 1, fontWeight: 700, fontSize: '10px' }}>
                        SMS are currently blocked.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          {storageUsage && (
            <>
              {this.renderStatCard("S3 Storage", storageUsage.summary.totalSizeHuman, <StorageIcon />, "#fb6340", () => this.handleOpenChart("storage"))}
            </>
          )}
        </Grid>
      </Box>
    )
  }

  renderAppointmentBookings() {
    const { dashboardCounts: { counts } } = this.props
    const appointmentBookings = counts.appointmentBooking

    if (!appointmentBookings || !Array.isArray(appointmentBookings) || appointmentBookings.length === 0) return null

    const totalBookings = appointmentBookings.length
    const metricCards = [
      { label: 'Total Bookings', value: totalBookings, color: '#5e72e4', icon: <AppointmentIcon />, period: 'monthly' }
    ]

    return (
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1f2937' }}>
          Appointment Bookings
        </Typography>
        <Grid container spacing={4}>
          {metricCards.map((metric) => (
            this.renderStatCard(metric.label, metric.value, metric.icon, metric.color, () => this.handleOpenAppointmentChart(metric.period))
          ))}
        </Grid>
      </Box>
    )
  }

  render() {
    const { auth: { user }, dashboardCounts: { isLoading, counts }, classes } = this.props
    const { settings, initialLoading } = this.state
    const { isStoreSubscribed } = settings;

    // Calculate lead status counts from crmData
    const leadStatusCounts = this.calculateLeadStatusCounts()

    const hasDailyOps = isStoreSubscribed && (
      (counts.order?.length || 0) > 0 ||
      (counts.payment?.length || 0) > 0 ||
      (settings.isTableReservationEnabled && (counts.tableBooking?.length || 0) > 0) ||
      (settings.isQrDiningEnabled && (counts.qrOrders?.length || 0) > 0)
    );
    const hasCatalog = isStoreSubscribed && ((counts.product || 0) > 0 || (counts.category || 0) > 0);
    const hasCustomers = isStoreSubscribed && ((counts.customer?.length || 0) > 0 && user.role === params.user.roles.admin.key);
    const hasCRM = settings.showGrowthMenu && Object.keys(leadStatusCounts).length > 0 && user.role === params.user.roles.admin.key;
    const hasAppointments = settings.showGrowthMenu;
    const hasUsage = (user.role === params.user.roles.admin.key && counts.storageUsage) || !!counts.emailUsage || !!counts.smsUsage;
    const hasAnySection = hasDailyOps || hasCatalog || hasCustomers || hasCRM || hasAppointments || hasUsage;

    // Show loader during initial load or when Redux isLoading is true
    const showLoader = initialLoading || isLoading;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: '#f8fafc' }}>
        <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 4, gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', letterSpacing: '-0.025em' }}>
                Dashboard
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Welcome back, {user.name}! Here's what's happening today.
              </Typography>
            </Box>
            <Button
              onClick={this.refresh()}
              variant="outlined"
              startIcon={<RefreshIcon />}
              size="medium"
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#475569',
                bgcolor: '#fff',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#cbd5e1',
                  color: '#1e293b'
                },
              }}
            >
              Refresh
            </Button>
          </Box>

          <Section style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto", paddingBottom: 40, scrollbarWidth: 'none' }}>
            {showLoader ? (
              <BrandedLoading />
            ) : hasAnySection ? (
              <Container maxWidth="xl" disableGutters>
                {hasDailyOps && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1f2937' }}>
                      Daily Operations
                    </Typography>
                    <Grid container spacing={2}>
                      {/* First Row: Sales, Orders, Payments */}
                      {this.renderStatCard("-", "Sales", <SalesIcon />, "#f5365c", () => this.handleOpenChart("sales"))}
                      {counts.order?.length > 0 &&
                        this.renderStatCard("Orders", counts.order.length, <OrderIcon />, "#fb6340", () => this.handleOpenChart("orders"))
                      }
                      {counts.payment?.length > 0 &&
                        this.renderStatCard("Payments", counts.payment.length, <PaymentIcon />, "#2dce89", () => this.handleOpenChart("payments"))
                      }
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {/* Second Row: Table Booking, QR Dining */}
                      {hasCustomers && this.renderStatCard("Total Customers", counts.customer.length, <CustomerIcon />, "#5e72e4", () => this.handleOpenChart("customers"))}
                      {settings.isTableReservationEnabled && counts.tableBooking?.length > 0 &&
                        this.renderStatCard("Table Booking", counts.tableBooking.length, <TableIcon />, "#11cdef", () => this.handleOpenChart("tableBookings"))
                      }
                      {settings.isQrDiningEnabled && counts.qrOrders?.length > 0 &&
                        this.renderStatCard("QR Dining", counts.qrOrders.length, <QrIcon />, "#5e72e4", () => this.handleOpenChart("qrOrders"))
                      }
                    </Grid>
                  </Box>
                )}

                {hasCatalog && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1f2937' }}>
                      Catalog Summary
                    </Typography>
                    <Grid container spacing={2}>
                      {counts.category > 0 &&
                        this.renderStatCard("Categories", counts.category, <CategoryIcon />, "#8898aa", null)
                      }
                      {counts.product > 0 &&
                        this.renderStatCard("Products", counts.product, <ProductIcon />, "#8898aa", null)
                      }
                    </Grid>
                  </Box>
                )}

                {hasCRM && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1f2937' }}>
                      Acquisition & Conversion Insights
                    </Typography>
                    <Grid container spacing={2}>
                      {this.renderStatCard("Contacts", leadStatusCounts['Contact'] || 0, <ContactIcon />, "#233dd2", () => this.handleOpenChart("crm", "Contact"))}
                      {this.renderStatCard("Leads", leadStatusCounts['Lead'] || 0, <LeadIcon />, "#ff9800", () => this.handleOpenChart("crm", "Lead"))}
                      {this.renderStatCard("Prospects", leadStatusCounts['Prospect'] || 0, <ProspectIcon />, "#e91e63", () => this.handleOpenChart("crm", "Prospect"))}
                      {this.renderStatCard("Customers", leadStatusCounts['Customer'] || 0, <CustomerStatusIcon />, "#4caf50", () => this.handleOpenChart("crm", "Customer"))}
                    </Grid>
                  </Box>
                )}

                {hasAppointments && this.renderAppointmentBookings()}

                {hasUsage && this.renderUsageOverview()}

              </Container>
            ) : (
              <></>
            )}
          </Section>
          {this.renderChartDialog()}
          {this.renderAppointmentChartDialog()}
        </Box>
        <Footer />
      </Box>
    )
  }
}

// Component Properties
Dashboard.propTypes = {
  auth: PropTypes.object.isRequired,
  dashboardCounts: PropTypes.object.isRequired,
  getDashboardCounts: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  settingDetail: PropTypes.func.isRequired
}

// Component State
function dashboardState(state) {
  return {
    auth: state.auth,
    dashboardCounts: state.dashboardCounts
  }
}

export default connect(dashboardState, {
  getDashboardCounts,
  settingDetail
})(withStyles(styles)(Dashboard))